import type { ServiceJournalKind } from '../types';
import type { CockpitPeriod } from './cockpitTypes';
import { roundCents } from './money';
import { isBackfilledAltEntry, isPostingEntry } from './serviceLogUtils';

// Reine Rechenkerne des Finanz-Cockpits (AdminDashboard "AGGREGATION ENGINE").
//
// WARUM EIGENES MODUL: Die Aggregation steckte komplett in einem useMemo einer
// ~1900 Zeilen langen Komponente und war damit ohne DOM-Testumgebung nicht
// pruefbar — im Projekt gibt es kein jsdom. Hier liegen nur die Entscheidungen,
// die tatsaechlich falsch sein koennen: In welchen Balken faellt ein Eintrag,
// ist er Arbeit oder Verbuchung, was ist er wert. Keine React-, DOM- oder
// Supabase-Abhaengigkeit (Aufbau wie companyMergeLogic.ts).
//
// KERNREGEL (Bugfix): Erbrachte Leistung zaehlt zum LEISTUNGSDATUM (`date`),
// nicht zum Zeitpunkt der Verbuchung (`bookedAt`). Nachtraeglich erfasste
// Eintraege — Reparatur-SQL, ALT-Backfill, Sammelbuchungen — tragen ein
// historisches `date` und ein `bookedAt` von heute. Nach `bookedAt` gebucht
// landeten sie im laufenden Monat und blaehten ihn auf, waehrend die Detailzeile
// daneben das echte (alte) Datum druckte. Balken und angezeigtes Datum stammen
// deshalb ab jetzt aus DEMSELBEN Feld.
//
// Die Verbuchung (Abrechnung) bleibt bewusst nach `bookedAt` gebucht: eine
// Rechnung gehoert in den Monat, in dem sie gestellt wurde.

/** Minimalschnitt eines Leistungsprotokoll-Eintrags, den das Cockpit braucht. */
export interface CockpitLogEntry {
  /** Nur zum Nachschlagen der Buchungsart im Register (serviceLogUtils). */
  id?: string;
  /** Die Buchungsart, wenn die Zeile aus dem Journal stammt. */
  kind?: ServiceJournalKind;
  date?: string;
  bookedAt?: string;
  bookingReference?: string;
  description?: string;
  /**
   * Der eingetragene Ausfuehrende — hier nur, um die MIGRATIONS-Sammelbuchung
   * zu erkennen (isMigrationBulkBackfill). Sie traegt einen System-Stub statt
   * eines Menschen; die Zuordnung zu einer Person macht die Engine selbst.
   */
  performerName?: string;
  /**
   * Die gebuchte Menge. Bewusst `unknown`: im jsonb stehen Mengen seit jeher
   * auch als String ('2'). Gelesen wird ueber `altQuantity`, nie direkt.
   */
  quantity?: unknown;
  /** Nur auf `ALT-`-Zeilen: die Menge, die wirklich auf den Stand gewirkt hat. */
  appliedUsed?: unknown;
}

/** Zeitfenster des Cockpit-Charts (Jahr → Monate, Quartal, Monat → Tage). */
export type CockpitTimeframe = 'year' | 'quarter' | 'month';

/** Kalendertag ohne Uhrzeit und ohne Zeitzone. `month` ist 0-11 wie bei Date. */
export interface CalendarDay {
  year: number;
  month: number;
  day: number;
}

const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/**
 * Laenge eines Monats (`m` ist 0-11 wie bei Date).
 *
 * Exportiert, weil die Engine denselben Kalender braucht: die synthetische
 * Offset-Zeile wird auf den LETZTEN Tag ihres Monats datiert, und ein
 * `new Date(year, month + 1, 0)` dafuer laese den Tag wieder in Ortszeit.
 */
export const daysInMonth = (y: number, m: number): number =>
  m === 1 ? (isLeapYear(y) ? 29 : 28) : [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m];

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Kalendertag aus dem ISO-Praefix `YYYY-MM-DD` — streng geprueft.
 *
 * WARUM NICHT `new Date(iso)`: Das normalisiert stillschweigend. `new Date(
 * '2026-02-31')` ist kein Fehler, sondern der 3. Maerz. Ein Eintrag mit kaputtem
 * Leistungsdatum landete damit in einem Monat, in dem nichts passiert ist,
 * statt wie vorgesehen auf `bookedAt` zurueckzufallen. Ausserdem liest
 * `Date.getMonth()` in Ortszeit: bei reinen Tagesangaben ("2026-08-01", von
 * V8 als UTC-Mitternacht gelesen) kippt der Monatsanfang westlich von
 * Greenwich in den Vormonat. Beide Fallen verschwinden, wenn der Kalendertag
 * direkt aus den Ziffern kommt.
 *
 * Volle Zeitstempel ("2026-08-01T00:00:00.000Z") und reine Tage ("2026-08-01")
 * ergeben denselben Tag; alles andere ist "kein Datum".
 */
export const calendarDay = (iso?: string | null): CalendarDay | null => {
  if (typeof iso !== 'string') return null;
  const m = ISO_DAY.exec(iso.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
};

/** Gibt den String nur zurueck, wenn daraus ein echter Kalendertag wird. */
const usable = (iso?: string | null): string | undefined => (calendarDay(iso) ? (iso as string) : undefined);

/** Datum einer ERBRACHTEN Leistung: Leistungsdatum zuerst, Verbuchung nur als Notnagel. */
export const performedDateIso = (log: CockpitLogEntry | null | undefined): string | undefined =>
  usable(log?.date) ?? usable(log?.bookedAt);

/** Datum einer VERBUCHUNG: Buchungszeitpunkt zuerst, Leistungsdatum nur als Notnagel. */
export const billedDateIso = (log: CockpitLogEntry | null | undefined): string | undefined =>
  usable(log?.bookedAt) ?? usable(log?.date);

/** Monatsindex 0-11 innerhalb von `year`; `null` bei fehlendem Datum oder anderem Jahr. */
const monthIn = (day: CalendarDay | null, year: number): number | null =>
  day && day.year === year ? day.month : null;

/** Monat, in dem eine erbrachte Leistung zaehlt. */
export const performedMonth = (log: CockpitLogEntry | null | undefined, year: number): number | null =>
  monthIn(calendarDay(performedDateIso(log)), year);

/** Monat, in dem eine Verbuchung zaehlt. */
export const billedMonth = (log: CockpitLogEntry | null | undefined, year: number): number | null =>
  monthIn(calendarDay(billedDateIso(log)), year);

/**
 * Balkenindex im Chart-Raster, `-1` wenn der Eintrag nicht ins Zeitfenster faellt.
 *
 * Im Jahresfenster ist der Balken genau der Monat — deshalb geht dieser Fall
 * durch dieselbe Funktion wie die Monatszuordnung der Mitarbeiterzeile.
 */
const indexIn = (
  day: CalendarDay | null,
  month: number | null,
  tf: CockpitTimeframe,
  year: number,
  displayedMonth: number,
): number => {
  if (tf === 'year') return month ?? -1;
  if (!day || day.year !== year) return -1;
  if (tf === 'quarter') return Math.floor(day.month / 3);
  return day.month === displayedMonth ? day.day - 1 : -1;
};

/** Balken, in dem eine erbrachte Leistung zaehlt. */
export const performedIndex = (
  log: CockpitLogEntry | null | undefined,
  tf: CockpitTimeframe,
  year: number,
  displayedMonth: number,
): number => indexIn(calendarDay(performedDateIso(log)), performedMonth(log, year), tf, year, displayedMonth);

/** Balken, in dem eine Verbuchung zaehlt. */
export const billedIndex = (
  log: CockpitLogEntry | null | undefined,
  tf: CockpitTimeframe,
  year: number,
  displayedMonth: number,
): number => indexIn(calendarDay(billedDateIso(log)), billedMonth(log, year), tf, year, displayedMonth);

/**
 * Echte Arbeit oder blosse Verbuchung?
 *
 * Die UMKEHRUNG von `isPostingEntry` (serviceLogUtils) — eine Buchung ist
 * keine Arbeit, alles andere ist welche. Damit entscheidet auch hier die
 * Journal-Buchungsart, sobald sie bekannt ist (aus dem Eintrag selbst oder aus
 * dem Buchungsart-Register); sonst gilt der Praefix- und Textrueckfall.
 * Cockpit, Leistungsnachweis und Abrechnung muessen dieselben Eintraege als
 * Arbeit zaehlen, sonst widerspricht der Beleg der Auswertung.
 *
 * WARUM BEIDE HAELFTEN: Die Nachbuchung aus dem Abrechnungsdialog traegt die
 * Referenz "MANUAL-JJJJ-NNNN" und erhoeht ausschliesslich `quantityBilled` —
 * die zugehoerige Arbeit steht bereits als eigener Eintrag im Protokoll. Wer
 * nur auf "BU-" prueft, zaehlt sie ein zweites Mal als Leistung: ERBRACHT und
 * Begehungszahl verdoppeln sich, und weil die Beschreibung "manuelle
 * verbuchung" enthaelt, schreibt das Cockpit den erfundenen Betrag auch noch
 * dem Bauleiter gut. Die Referenz allein reicht nicht, die Beschreibung allein
 * auch nicht — es braucht beide. `isPostingEntry` traegt genau das.
 *
 * "MANUAL-" ohne diese Beschreibung und "LEGACY-AUTO" bleiben echte Arbeit.
 *
 * `ALT-` bleibt ebenfalls Arbeit: die Uebernahme aus einer Alt-Rechnung ist
 * keine Buchung (isBackfilledAltEntry, bewusst nicht Teil von
 * `isAccountingEntry`) — sie wurde erbracht, nur eben frueher berechnet.
 *
 * `VZ-` (Verzicht) und `RN-` (Ruecknahme) sind KEINE Arbeit. Im Cockpit
 * aendert das nichts: beide tragen Menge 0 und kommen hinter dem Mengen-Gate
 * in components/AdminDashboard.tsx:278 (`if(!log.quantity||log.quantity<=0)`)
 * ohnehin nie hier an.
 *
 * Der Beleg wird vor der Frage normalisiert (trimmen, Grossschreibung): das
 * Cockpit liest Altbestand, in dem Referenzen mit Leerzeichen und in
 * Kleinschreibung vorkommen, und hat sie seit jeher mitgezaehlt.
 *
 * AENDERT das Normalisieren den Beleg, wird OHNE `id` und `kind` gefragt — das
 * Buchungsart-Register bleibt dann aussen vor. Die Journal-Ableitung
 * normalisiert ihrerseits nicht (serviceJournal.journalKind, byte-gleich mit
 * SQL Nr. 60): ein gepolsterter oder kleingeschriebener Beleg trifft dort kein
 * Praefix, die Zeile wird als `work` gefuehrt und als `work` registriert. Mit
 * `id` faende `isPostingEntry` genau diese Eintragung und naehme die Buchung
 * als Arbeit in ERBRACHT auf. Fuer solche Altbestandszeilen entscheidet
 * deshalb weiterhin der normalisierte Beleg, nicht das Register.
 */
export const isWorkEntry = (log: CockpitLogEntry | null | undefined): boolean => {
  const roh = log?.bookingReference || '';
  const norm = roh.trim().toUpperCase();
  // Unveraendert: die Zeile darf mit id und kind fragen.
  if (norm === roh) return !isPostingEntry(log);
  return !isPostingEntry({ bookingReference: norm, description: log?.description });
};

/** Wert eines Eintrags. Fehlende Menge oder fehlender Preis ergeben 0, nie NaN. */
export const entryAmount = (quantity?: number | null, unitPrice?: number | null): number =>
  (Number(quantity) || 0) * (Number(unitPrice) || 0);

// ═══════════════════════════════════════════════════════════════════════════
// Zeitraum und Geld — die kleinen reinen Helfer der Engine (cockpitEngine.ts)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Monatsschluessel `JJJJ-MM` aus einem ISO-Datum, oder `null`.
 *
 * Geht ueber `calendarDay` und NICHT ueber `iso.slice(0, 7)`: der Schnitt
 * nimmt auch '2026-13' und '2026-02-31' an und legte damit einen Monat an,
 * den es nicht gibt. Belegmonate und Leistungsmonate muessen nach derselben
 * Regel entstehen, sonst zeigt dieselbe Zeile im Chart und im Drilldown
 * verschiedene Monate.
 */
export const monthKey = (iso?: string | null): string | null => {
  const day = calendarDay(iso);
  if (!day) return null;
  return `${String(day.year).padStart(4, '0')}-${String(day.month + 1).padStart(2, '0')}`;
};

/**
 * Die Monatsindizes (0–11) eines Zeitraums.
 *
 * Der Kern von Entscheidung 4 des Umbauplans: die Engine rechnet IMMER zwoelf
 * Monate, der Zeitraum waehlt nur aus, welche davon in die Kacheln summiert
 * werden. Vorher definierte der Schalter die Kacheln um ("Monat" hiess "bis
 * heute"), und Forecast und Realisierung sprangen beim Umschalten.
 *
 * Unbrauchbare Angaben werden gedeckelt statt zurueckgewiesen: ein Quartal 0
 * oder ein Monat 13 aus einem alten gespeicherten Zustand soll das Cockpit
 * nicht leer lassen.
 */
export const periodMonthIndices = (period: CockpitPeriod): number[] => {
  if (period.kind === 'quarter') {
    const q = Math.min(4, Math.max(1, Math.round(Number(period.quarter) || 1)));
    const first = (q - 1) * 3;
    return [first, first + 1, first + 2];
  }
  if (period.kind === 'month') {
    const m = Math.min(12, Math.max(1, Math.round(Number(period.month) || 1)));
    return [m - 1];
  }
  return Array.from({ length: 12 }, (_, i) => i);
};

/**
 * Wert eines Eintrags in ganzzahligen CENT.
 *
 * Die Engine rechnet durchgehend in Cent (Plan-Entscheidung 10). Gerundet wird
 * JE EINTRAG und nicht erst in der Summe: sonst schleppt jede Kachel den
 * Float-Rest von zehntausend Zeilen mit, und zwei Ansichten derselben Zahl
 * (Kachel und Drilldown) weichen in den letzten Stellen voneinander ab.
 *
 * `roundCents` statt `Math.round`, weil negative Betraege (Storno, Ruecknahme)
 * symmetrisch gerundet werden muessen — `Math.round(-0.5)` ergibt -0.
 */
export const entryCents = (quantity?: number | null, unitPrice?: number | null): number =>
  roundCents(entryAmount(quantity, unitPrice) * 100);

/**
 * Hat diese Zeile wirklich `appliedUsed` statt der Belegmenge genommen?
 *
 * EINE Stelle fuer die Bedingung, weil zwei Auswerter sie brauchen: die MENGE
 * (`altQuantity`) und der Hinweis „Alt-Uebernahme, angewandte Menge" an der
 * Detailzeile der Engine. Standen beide getrennt da, erklaerte die Detailzeile
 * frueher oder spaeter eine Menge, die gar nicht genommen wurde.
 *
 * EIN LEERER TEXT IST KEIN WERT. `Number('')` und `Number('   ')` sind 0 —
 * dieselbe Falle wie bei `null`, nur eine Stufe versteckter: im jsonb steht
 * ein geleertes Feld als `''`, und eine ALT-Zeile mit `appliedUsed: ''` und
 * Menge 4 wurde damit als „0 Stueck angewandt" gelesen und verschwand aus
 * ERBRACHT. Leer, fehlend und unlesbar heissen deshalb alle dasselbe:
 * `appliedUsed` ist nicht da.
 */
export const usesAppliedQuantity = (log: CockpitLogEntry | null | undefined): boolean => {
  const applied = log?.appliedUsed;
  if (applied === undefined || applied === null) return false;
  if (typeof applied === 'string' && applied.trim() === '') return false;
  if (!Number.isFinite(Number(applied))) return false;
  return isBackfilledAltEntry(log);
};

/**
 * Menge eines Eintrags fuer den LEISTUNGSSTAND.
 *
 * Eine `ALT-`-Zeile (Uebernahme aus einer Alt-Rechnung) traegt in `quantity`
 * die BELEGmenge; `appliedUsed` sagt, wie viel davon wirklich auf den Stand
 * gewirkt hat (types.ServiceTransaction.appliedUsed). War ein Teil bereits
 * protokolliert, ist die Belegmenge groesser — wer sie nimmt, haelt die
 * Position fuer ausgeschoepft.
 *
 * `appliedUsed` gilt nur, wenn es wirklich eine Zahl ist (siehe
 * `usesAppliedQuantity`). Fehlendes, leeres oder unlesbares `appliedUsed`
 * faellt auf `quantity` zurueck — nie auf 0.
 *
 * EINE Definition fuer Engine und Forecast: beide muessen dieselbe Menge
 * sehen, sonst plant der Forecast eine Restmenge, die die Engine nicht kennt.
 */
export const altQuantity = (log: CockpitLogEntry | null | undefined): number =>
  usesAppliedQuantity(log) ? Number(log?.appliedUsed) : Number(log?.quantity) || 0;

/**
 * Ein Eintrag aus der MIGRATIONS-Sammelbuchung — kein Monatsumsatz.
 *
 * Beim Umzug in dieses System wurde je Position EINE Zeile ueber den gesamten
 * Altbestand angelegt und kuenstlich auf den 01.12.2025 datiert: Beschreibung
 * „Altbestand/bereits abgerechnete Leistung (Sammelnachtrag)", Ausfuehrender
 * „System (Migration)". Die Menge ist echt, das Datum ist es nicht: sie
 * verteilt sich ueber Jahre. Wer sie in einen Monat legt, baut dort einen
 * Balken, der nie eine Leistung dieses Monats war — und im Dezember eine
 * Spitze, an der jeder Jahresvergleich scheitert.
 *
 * Deshalb wird sie NIE als erbrachte Leistung gezaehlt, sondern als Warnung
 * `legacy_backfill` ausgewiesen: sichtbar, nachvollziehbar, aber nicht in der
 * Zeitreihe.
 *
 * ENG GEFASST, und zwar absichtlich. Das Wort „Sammelnachtrag" allein reicht
 * NICHT, denn es traegt einen zweiten, voellig anderen Vorgang: der
 * Mengen-Nachtrag aus der Abrechnungsansicht schreibt echte, vom Anwender
 * datierte Arbeitszeilen mit dem Ausfuehrenden
 * `invoiceLogic.SYSTEM_BACKLOG_ACTOR` („System (Sammelnachtrag)") und der
 * Beschreibung „Menge ohne Protokolleintrag nachgetragen (Sammelnachtrag, 3
 * Stk)" (invoiceLogic.planUndatedBacklogEntries, geschrieben von
 * undatedBacklogService). Das ist ERBRACHTE LEISTUNG des jeweiligen Monats.
 * Wurde sie hier mitgefangen, fehlte sie in jedem Balken.
 *
 * Bleibt genau die Migrationsform, und die verlangt IMMER den Altbestands-Text
 * plus eine der beiden Herkunftsangaben:
 *   1. Bestandsuebernahme (`isBackfilledAltEntry`: Buchungsart
 *      `legacy_backfill` oder Praefix `ALT-`), oder
 *   2. den System-Stub „System (Migration)" als Ausfuehrenden.
 *
 * ABGRENZUNG zur gewoehnlichen `ALT-`-Zeile: die ist die Uebernahme EINER
 * Alt-Rechnung mit ihrem echten Leistungsdatum und zaehlt unveraendert (Plan
 * §5, Menge ueber `altQuantity`). Nur zusammen mit dem Altbestands-Text wird
 * daraus die Migrationszeile.
 */
const MIGRATION_ACTOR = /^system\s*\(\s*migration\s*\)$/i;
const ALTBESTAND_TEXT = /altbestand/i;

export const isMigrationBulkBackfill = (log: CockpitLogEntry | null | undefined): boolean => {
  if (!ALTBESTAND_TEXT.test(String(log?.description ?? ''))) return false;
  if (isBackfilledAltEntry(log)) return true;
  return MIGRATION_ACTOR.test(String(log?.performerName ?? '').trim());
};
